import { EventEmitter, Injectable, NgZone } from '@angular/core';

import { environment } from '@environment';

import { PlatformService } from '../util/platform.service';
import { StorageService } from '../util/storage.service';
import { LocalStorageService } from '../util/local-storage.service';
import { SentryBreadcrumbsService } from '../sentry-breadcrumbs.service';
import { UtilityService } from '../util/utility.service';
import { SessionStorageService } from '../util/session-storage.service';

declare global {
    interface Window {
        dataLayer: any;
    }
}

@Injectable({
    providedIn: 'root'
})
export class GtmService {
    pageDetailsLogged = false;
    sdkLoaded = false;
    gtmSdkLoaded: EventEmitter<boolean> = new EventEmitter(false);
    isProduction = false;

    constructor(
        private _ngZone: NgZone,
        private storageService: StorageService,
        private platformService: PlatformService,
        private localStorage: LocalStorageService,
        private sentryBreadcrumbsService: SentryBreadcrumbsService,
        private utilityService: UtilityService,
        private sessionStorageService: SessionStorageService
    ) {
        this.isProduction = environment.NODE_ENV === 'production';
    }

    logPageDetails(data: any, referrer: string) {
        if (this.pageDetailsLogged || !this.isProduction) {
            return;
        }

        this.pageDetailsLogged = true;
        this.gtmSdkLoaded.subscribe(() => {
            this.sdkLoaded = true;
            const outlet = data.outlet;
            const isServicable = !outlet.dflt;
            const outletStatus = this.utilityService.getOutletState(outlet);
            const isLoggedIn = this.storageService.get('session-id') && this.storageService.get('phone_no');
            const locationType = this.storageService.get('outlet_locality') === 'Showing Default Location' ? 'Default' : 'User';

            const entries = performance.getEntriesByType('navigation');
            entries.forEach((entry: any) => {
                if (entry.type === 'navigate') {
                    const extraParams = {
                        value1: 'location_serviceable: ' + isServicable,
                        value2: 'location_type: ' + locationType,
                        value3: 'outlet_status: ' + outletStatus,
                        value4: 'login_status: ' + (isLoggedIn ? 'signed' : 'unsigned')
                    };

                    this.logEventTracking(referrer, 'page_details', null, extraParams);
                }
            });
        });
    }

    logEvent(event_name: string, parameters: { [key: string]: string | number | boolean }) {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        try {
            this.sentryBreadcrumbsService.add('gtm', 'Logging analytics Event', {
                dataLayerPresent: Boolean(window?.dataLayer),
                gtmSdkLoaded: this.sdkLoaded
            });

            this.resetDataLayer();
            this._ngZone.runOutsideAngular(() => {
                const dataLayer = window.dataLayer || [];
                const featureEvent = {
                    event: event_name,
                    ...parameters
                };

                dataLayer.push(featureEvent);
            });
        } catch (e) {
            const err = new Error(e);
            this.sentryBreadcrumbsService.add('GTM', 'GTM Error', err);
            throw err;
        }
    }

    logEventTracking(
        referrer: string,
        event_action: string,
        event_label: string | number | boolean = null,
        extraParams: any = {}
    ) {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        this.resetDataLayer();
        this._ngZone.runOutsideAngular(() => {
            const dataLayer = window.dataLayer || [];
            const featureEvent = {
                event: 'event_tracking',
                referrer,
                event_action,
                event_label,
                ...extraParams
            };
            dataLayer.push(featureEvent);
        });
    }

    logWebAnalyticsEvent(event_action: string, event_label: string | number | boolean, cta_clicked: string = null) {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        this.resetDataLayer();
        this._ngZone.runOutsideAngular(() => {
            const dataLayer = window.dataLayer || [];
            const featureEvent = {
                event: 'web_analytics',
                event_action,
                event_label,
                cta_clicked
            };
            dataLayer.push(featureEvent);
        });
    }

    logPaymentEvent(event_action: string, event_label: string | number | boolean = null) {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        this.resetDataLayer();
        this._ngZone.runOutsideAngular(() => {
            const dataLayer = window.dataLayer || [];
            const featureEvent = {
                event: 'payment_event',
                event_action,
                event_label
            };
            dataLayer.push(featureEvent);
        });
    }

    resetDataLayer() {
        const dataLayerVariables = {
            value1: null,
            value2: null,
            value3: null,
            value4: null,
            referrer: null,
            timestamp: null,
            event_label: null,
            cta_clicked: null,
            event_value: null,
            event_action: null
        };

        this._ngZone.runOutsideAngular(() => {
            const dataLayer = window.dataLayer || [];
            dataLayer.push(dataLayerVariables);
        });
    }

    addToCart(category: any, product: any, productSize: any, quantity: number, brand: string) {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        this._ngZone.runOutsideAngular(() => {
            const { prices } = productSize;
            const { id, label, subcategory_name } = product;

            const categoryValue = category?.name || '';
            const dataLayer = window.dataLayer || [];

            const ecommerceData = {
                currency: 'INR',
                value: prices.discounted_price,
                items: [
                    {
                        item_id: id,
                        item_name: label,
                        quantity,
                        price: productSize.price,
                        item_category: categoryValue,
                        item_variant: productSize.label,
                        item_category2: subcategory_name,
                        item_brand: `${environment.brand.name} | ${brand}`
                    }
                ]
            };

            const addToCartEvent = {
                event: 'add_to_cart',
                ecommerce: ecommerceData
            };

            dataLayer.push({ ecommerce: null }, addToCartEvent);
        });
    }

    removeFromCart(category: any, product: any, productSize: any, quantity: number, brand: string) {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        this._ngZone.runOutsideAngular(() => {
            quantity = quantity || 1;
            const { prices } = productSize;
            const { id, label, subcategory_name } = product;

            const categoryValue = category?.name || '';
            const dataLayer = window.dataLayer || [];

            const ecommerceData = {
                currency: 'INR',
                value: prices.discounted_price,
                items: [
                    {
                        item_id: id,
                        item_name: label,
                        quantity: quantity,
                        price: productSize.price,
                        item_category: categoryValue,
                        item_variant: productSize.label,
                        item_category2: subcategory_name,
                        item_brand: `${environment.brand.name} | ${brand}`
                    }
                ]
            };

            const removeFromCartEvent = {
                event: 'remove_from_cart',
                ecommerce: ecommerceData
            };

            dataLayer.push({ ecommerce: null }, removeFromCartEvent);
        });
    }

    purchase(tracking_id: string) {
        if (this.platformService.isServer()) {
            return;
        }

        try {
            const cart = this.sessionStorageService.get('cart');
            this._ngZone.runOutsideAngular(() => {
                const filteredItems = cart.items.filter((item: any) => item.type === 'MenuItem');
                const items = filteredItems.map(({ id, quantity, cost }) => {
                    const { product, brand, category, product_size, price } = this.getProductDetails(id, quantity, cost);

                    return {
                        item_id: product.id,
                        quantity: quantity || 1,
                        item_name: product.label,
                        item_variant: product_size.label,
                        price: price || product_size.price,
                        item_category: category?.name || '',
                        item_brand: brand
                    };
                });

                const dataLayer = window?.dataLayer || [];
                const ecommerceData = {
                    items,
                    currency: 'INR',
                    value: cart.total_payable,
                    transaction_id: tracking_id,
                    coupon: cart.discount_code || '',
                    shipping: cart.outlet_service_charges.reduce((total: number, item: any) => total + item.amount, 0)
                };

                const purchaseEvent = {
                    event: 'purchase',
                    ecommerce: ecommerceData
                };

                dataLayer.push({ ecommerce: null }, purchaseEvent);
            });
        } catch (e) {
            console.log('ECOMM:: An error has occurred while purchase.');
        }
    }

    logPassUpdation(operation, item) {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        this._ngZone.runOutsideAngular(() => {
            const dataLayer = window.dataLayer || [];

            const ecommerceData = {
                currency: 'INR',
                value: item.price,
                items: [
                    {
                        item_id: item.ids.product_id,
                        item_name: item.label,
                        quantity: 1,
                        price: item.marked_price,
                        item_variant: 'Subscription Item',
                        item_brand: environment.brand.name
                    }
                ]
            };

            const cartEvent = {
                event: operation === 'add' ? 'add_to_cart' : 'remove_from_cart',
                ecommerce: ecommerceData
            };

            dataLayer.push({ ecommerce: null }, cartEvent);
        });
    }

    beginCheckout(cart: any) {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        try {
            this._ngZone.runOutsideAngular(() => {
                const filteredItems = cart.items.filter((item: any) => item.type === 'MenuItem');
                const items = filteredItems.map(({ id, quantity, cost }) => {
                    const { product, brand, category, product_size, price } = this.getProductDetails(id, quantity, cost);

                    return {
                        item_id: product.id,
                        quantity: quantity || 1,
                        item_name: product.label,
                        item_variant: product_size.label,
                        price: price || product_size.price,
                        item_category: category?.name || '',
                        item_brand: `${environment.brand.name} | ${brand}`
                    };
                });

                const dataLayer = window?.dataLayer || [];
                const { total_payable, discount_code } = cart;
                const ecommerceData = {
                    items,
                    currency: 'INR',
                    value: total_payable,
                    coupon: discount_code
                };

                const beginCheckoutEvent = {
                    event: 'begin_checkout',
                    ecommerce: ecommerceData
                };

                dataLayer.push({ ecommerce: null }, beginCheckoutEvent);
            });
        } catch (e) {
            console.log('ECOMM:: An error has occoured while checkout.');
            this.logEvent('js_errors', {
                event_label: 'Ecommerce Error',
                event_value: `beginCheckout: ${e}`
            });
        }
    }

    addShippingInfo(cart: any) {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        try {
            this._ngZone.runOutsideAngular(() => {
                const filteredItems = cart.items.filter((item: any) => item.type === 'MenuItem');
                const items = filteredItems.map(({ id, quantity, cost }) => {
                    const { product, brand, category, product_size, price } = this.getProductDetails(id, quantity, cost);

                    return {
                        item_id: product.id,
                        quantity: quantity || 1,
                        item_name: product.label,
                        item_variant: product_size.label,
                        price: price || product_size.price,
                        item_category: category?.name || '',
                        item_brand: `${environment.brand.name} | ${brand}`
                    };
                });

                const dataLayer = window?.dataLayer || [];
                const { total_payable, discount_code } = cart;
                const ecommerceData = {
                    items,
                    currency: 'INR',
                    value: total_payable,
                    coupon: discount_code
                };

                const addShippingInfoEvent = {
                    event: 'add_shipping_info',
                    ecommerce: ecommerceData
                };

                dataLayer.push({ ecommerce: null }, addShippingInfoEvent);
            });
        } catch (e) {
            console.log('ECOMM:: An error has occurred while sending shipping event.');
            this.logEvent('js_errors', {
                event_label: 'Ecommerce Error',
                event_value: `addShippingInfo: ${e}`
            });
        }
    }

    addPaymentInfo(option, cart: any) {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        try {
            this._ngZone.runOutsideAngular(() => {
                const filteredItems = cart.items.filter((item: any) => item.type === 'MenuItem');
                const items = filteredItems.map(({ id, quantity, cost }) => {
                    const { product, brand, category, product_size, price } = this.getProductDetails(id, quantity, cost);

                    return {
                        item_id: product.id,
                        quantity: quantity || 1,
                        item_name: product.label,
                        item_variant: product_size.label,
                        price: price || product_size.price,
                        item_category: category?.name || '',
                        item_brand: `${environment.brand.name} | ${brand}`
                    };
                });

                const dataLayer = window?.dataLayer || [];
                const { total_payable } = cart;
                const ecommerceData = {
                    items,
                    currency: 'INR',
                    value: total_payable,
                    payment_type: option.code
                };

                const addPaymentInfoEvent = {
                    event: 'add_payment_info',
                    ecommerce: ecommerceData
                };

                dataLayer.push({ ecommerce: null }, addPaymentInfoEvent);
            });
        } catch (e) {
            console.log('ECOMM:: An error has occoured while payment.');
            this.logEvent('js_errors', {
                event_label: 'Ecommerce Error',
                event_value: `addPaymentInfo: ${e}`
            });
        }
    }

    getProductDetails(current_item_id: string, quantity: number, price: number) {
        const localCart = this.localStorage.get('cart');
        const { items, itemDetails } = localCart;

        // sorting local cart item and current item customization ids string which we get from order_status api
        // since, order of customization can vary in local cart and customization string which we recieve from order_status api

        let item = null;

        const [outlet_id, item_menu_id, item_product_size_id, ...customization_ids] = current_item_id.split('-');
        const sortedItemId = [
            outlet_id,
            item_menu_id,
            item_product_size_id,
            ...customization_ids.sort((a, b) => parseInt(a) - parseInt(b))
        ].join('-');

        for (const key of Object.keys(items)) {
            const [outlet_id, menu_id, product_size_id, ...customization_ids] = key.split('-');
            const sortedLocalCartItemId = [
                outlet_id,
                menu_id,
                product_size_id,
                ...customization_ids.sort((a, b) => parseInt(a) - parseInt(b))
            ].join('-');

            if (sortedLocalCartItemId === sortedItemId) {
                item = items[key];
            }
        }

        if (!Object.keys(items).length) {
            throw 'Empty cart';
        } else if (!item) {
            throw current_item_id;
        }

        const { menu_id, product_id, product_size_id } = item.ids;

        const menu = itemDetails[menu_id];
        const { products, product_sizes, product_categories } = menu;

        const product = products[product_id];
        const product_size = product_sizes[product_size_id];

        const { category_id } = product;
        const category = product_categories[category_id];

        const brand = menu.brand_id;

        return {
            brand,
            product,
            category,
            quantity,
            product_size,
            price: Number(price / quantity)
        };
    }

    logLoginPath() {
        if (this.platformService.isServer() || !this.isProduction) {
            return;
        }

        this.resetDataLayer();
        const isCartPage = location.pathname === '/cart';
        const value = isCartPage ? 'LoginVia: CartPage' : 'LoginVia: Modal';
        this._ngZone.runOutsideAngular(() => {
            this.logEvent('event_tracking', {
                event_label: value
            });
        });
    }

    // log time of completion
    startTime: number;

    startTimer() {
        this.startTime = Date.now();
    }

    endTimer(startTime, event_name: string, parameters: { [key: string]: string | number | boolean }) {
        const endTime = Date.now();
        const elapsedTime = endTime - startTime;
        this.startTime = null;
        this.logEvent(event_name, {
            ...parameters,
            value1: Math.ceil(elapsedTime / 1000) //convert to ceil value in seconds
        });
    }
}
